import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
import { CommonPerformanceTableComponent } from '../../components/common-performance-table/common-performance-table.component';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { TaskStatus } from '../../models/bulk-search.models';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';
import { MatAngularModule } from '../../modules/mat-angular.module';


@Component({
  selector: 'app-generic-category-analysis-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    // CitationCardComponent,
    BarLineComboChartComponent,
    CompositeScoreWidgetComponent,
    CommonPerformanceTableComponent,
  ],
  templateUrl: './generic-category-analysis-tab.component.html',
  styleUrl: './generic-category-analysis-tab.component.scss'
})
export class GenericCategoryAnalysisTabComponent implements OnInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  
  // Core data properties
  categoryAnalysisResponse: any = null;
  /** Backfill / reload after overview-first bulk */
  categoryAnalysisBgLoad: { phase: 'idle' | 'queued' | 'running'; taskId: string | null } = {
    phase: 'idle',
    taskId: null
  };
  llmVisibilityTaskId: string | null = null;
  llmVisibilityProgress: TaskStatus | null = null;
  isLlmVisibilityLoading = false;
  brands: any[] = [];
  metadata: any = null;

  // Visibility and memory management
  isTabVisible: boolean = false;
  isChartRendered: boolean = false;
  shouldRenderChart: boolean = false;

  // Chart properties
  results: any[] = [];
  lineChart: any[] = [];

  // Category-specific chart data (from chart_data object)
  aiCategoryInsights: any = null;
  aiTableSummary: any = null;
  categoryDemand: any = null;
  subjectDemandAnalysis: any = null;

  // Composite Score
  compositeScore: number = 0;

  // Color schemes for charts
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };

  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  // Responsive view dimensions
  view: [number, number] = [600, 400];

  // Table properties
  tableColumns: any[] = [];
  tableRows: any[] = [];

  // Evidence sources
  evidenceSources: any[] = [];

  /** From overview `overview_prompt_collection` (same structure as former Overview section). */
  overviewPromptCollection: Record<string, unknown> | null = null;

  /** True when tab content is synthesized from prompt/response rows (not full Gemini category bulk). */
  categoryFromPromptResponses = false;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    // Subscribe to category analysis response
    const analysisSub = this.templateCommonService.categoryAnalysisResponse$.subscribe((response: any) => {
      if (response) {
        this.categoryAnalysisResponse = response;
        this.processCategoryAnalysisData(response);
        this.cdr.detectChanges();
      }
    });
    const bgSub = this.templateCommonService.categoryAnalysisBackgroundLoad$.subscribe((s) => {
      this.categoryAnalysisBgLoad = s;
      this.cdr.detectChanges();
    });
    const llmSub = this.templateCommonService.llmComparisonMetricsTaskId$.subscribe((id) => {
      this.llmVisibilityTaskId = id;
      this.isLlmVisibilityLoading = !!id;
      this.cdr.detectChanges();
    });
    const llmProgSub = this.templateCommonService.llmComparisonMetricsProgress$.subscribe((p) => {
      this.llmVisibilityProgress = p;
      this.cdr.detectChanges();
    });
    const overviewPcSub = this.templateCommonService.analysisResponse$.subscribe((data: any) => {
      if (data && data !== true && data.overview_prompt_collection && typeof data.overview_prompt_collection === 'object') {
        this.overviewPromptCollection = data.overview_prompt_collection as Record<string, unknown>;
        this.syncCategoryFromPromptCollection();
        this.cdr.detectChanges();
      }
    });

    // Subscribe to filterData changes to detect competitor additions/removals
    let previousEntityNames: string[] = [];
    // Initialize from current filterData if available
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    if (currentFilterData && currentFilterData.selectedEntities) {
      previousEntityNames = currentFilterData.selectedEntities.map((e: any) => e.name || e).sort();
    }
    const filterDataSub = this.templateCommonService.filterData$.subscribe((filterData: any) => {
      // Update previous entities for display/logging only; do NOT auto-trigger API here.
      if (filterData && filterData.selectedEntities) {
        const currentEntityNames = filterData.selectedEntities.map((e: any) => e.name || e).sort();
        previousEntityNames = currentEntityNames;
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, filterDataSub, bgSub, llmSub, llmProgSub, overviewPcSub);

    const ov = this.templateCommonService.getCurrentAnalysisResponse();
    if (ov?.overview_prompt_collection && typeof ov.overview_prompt_collection === 'object') {
      this.overviewPromptCollection = ov.overview_prompt_collection;
    }

    // Load data when component initializes
    this.loadCategoryAnalysisData();
    this.syncCategoryFromPromptCollection();

    // Check visibility after component initialization
    setTimeout(() => {
      this.checkTabVisibility();
    }, 100);

    // Update responsive view dimensions
    this.updateViewByBreakpoint();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewByBreakpoint, { passive: true });
    }
  }

  ngOnDestroy(): void {
    console.log('🗑️ Category Analysis: Destroying component and clearing memory...');
    
    // Clear chart data to free memory
    this.clearChartData();
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.categoryAnalysisResponse = null;
    this.brands = [];
    this.metadata = null;
    this.aiCategoryInsights = null;
    this.aiTableSummary = null;
    this.categoryDemand = null;
    this.subjectDemandAnalysis = null;
    
    // Reset visibility flags
    this.isTabVisible = false;
    this.isChartRendered = false;
    this.shouldRenderChart = false;
    
    console.log('✅ Category Analysis: Component destroyed and memory cleared');
  }

  /**
   * Clear chart data to reduce memory usage
   */
  private clearChartData(): void {
    this.results = [];
    this.lineChart = [];
    this.isChartRendered = false;
    this.shouldRenderChart = false;
  }

  /**
   * Check if tab is visible using Intersection Observer
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkTabVisibility();
  }

  /**
   * Check tab visibility and manage chart rendering
   */
  private checkTabVisibility(): void {
    if (!this.elementRef?.nativeElement) return;
    
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    
    if (isVisible !== this.isTabVisible) {
      this.isTabVisible = isVisible;
      console.log(`🔍 Category Analysis: Tab visibility changed to ${isVisible}`);
      
      if (isVisible) {
        this.onTabBecameVisible();
      } else {
        this.onTabBecameHidden();
      }
    }
  }

  /**
   * Handle tab becoming visible
   */
  private onTabBecameVisible(): void {
    console.log('👁️ Category Analysis: Tab became visible, rendering chart...');
    this.shouldRenderChart = true;
    this.renderChartIfNeeded();
  }

  /**
   * Handle tab becoming hidden
   */
  private onTabBecameHidden(): void {
    console.log('🙈 Category Analysis: Tab became hidden, clearing chart...');
    this.shouldRenderChart = false;
    this.clearChartData();
    this.cdr.detectChanges();
  }

  /**
   * Render chart only if tab is visible and data is available
   */
  private renderChartIfNeeded(): void {
    if (!this.shouldRenderChart || this.isChartRendered) return;
    
    if (this.brands && this.brands.length > 0) {
      console.log('📊 Category Analysis: Rendering chart with data...');
      this.prepareChartData();
      this.isChartRendered = true;
      this.cdr.detectChanges();
    }
  }

  loadCategoryAnalysisData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentCategoryAnalysisResponse();
    if (currentResponse) {
      console.log('Category Analysis: Found existing response, processing immediately');
      this.categoryAnalysisResponse = currentResponse;
      this.processCategoryAnalysisData(currentResponse);
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('Category Analysis: No data yet, waiting for subscription to receive data');
    }
  }

  // Method to trigger reload from parent component
  onReload(): void {
    this.templateCommonService.triggerTabReload('category-analysis', true);
  }

  reloadCategoryAnalysis(): void {
    this.templateCommonService.rebuildCategoryAnalysisFromSavedVisibility();
  }

  // Method to retry loading data (used in error state)
  retryLoad(): void {
    this.onReload();
  }

  /** Full category tab from API (brands + per-category scores). */
  private isRichCategoryPayload(data: any): boolean {
    if (!data) return false;
    const sd = data.structured_data ?? data;
    const brands = sd?.brands;
    if (!Array.isArray(brands) || brands.length === 0) return false;
    return brands.some(
      (b: any) => Array.isArray(b?.category_scores) && b.category_scores.length > 0
    );
  }

  private processCategoryAnalysisData(data: any): void {
    console.log('🔍 Category Analysis: Raw data received:', data);
    console.log('🔍 Category Analysis: Structured data:', data?.structured_data);

    this.categoryAnalysisResponse = data;
    this.categoryFromPromptResponses = false;

    let processedData = data?.structured_data || data;
    if (!data) {
      this.brands = [];
      this.syncCategoryFromPromptCollection();
      this.cdr.markForCheck();
      return;
    }

    if (!this.isRichCategoryPayload(data)) {
      const promptSd = this.buildStructuredDataFromPromptCollection();
      if (promptSd) {
        processedData = {
          ...processedData,
          ...promptSd,
          chart_data: { ...(processedData['chart_data'] || {}), ...(promptSd['chart_data'] || {}) }
        };
        this.categoryAnalysisResponse = {
          ...data,
          structured_data: processedData,
          model_meta: { ...(data.model_meta || {}), source: 'prompt_responses' }
        };
        this.categoryFromPromptResponses = true;
      }
    }

    console.log('🔍 Category Analysis: Processed data:', processedData);

    this.hydrateCategoryState(processedData, data);
  }

  /** Apply structured_data to charts/tables (shared: API payload or prompt-derived). */
  private hydrateCategoryState(processedData: any, data?: any): void {
    this.brands = processedData.brands || [];
    this.metadata =
      processedData.metadata ||
      processedData.model_meta ||
      data?.metadata ||
      this.categoryAnalysisResponse?.metadata ||
      null;

    this.evidenceSources = this.transformSourcesData(
      processedData.evidence_sources || data?.evidence_sources || []
    );

    if (processedData.chart_data) {
      this.aiCategoryInsights = processedData.chart_data.ai_category_insights;
      this.aiTableSummary = processedData.chart_data.ai_table_summary;
      this.categoryDemand = processedData.chart_data.category_demand;
      this.subjectDemandAnalysis = processedData.chart_data.subject_demand_analysis;
    }

    this.prepareTableData();
    this.updateCompositeScore();
    this.checkTabVisibility();

    const hasNewFlow = !!(processedData?.average_category_performance && this.brands?.length);
    if (this.isTabVisible || hasNewFlow) {
      this.prepareChartData();
      if (hasNewFlow) {
        this.shouldRenderChart = true;
      }
    }

    this.cdr.markForCheck();
  }

  /** When overview prompt collection arrives first (or category tab is minimal), fill tab from those rows. */
  private syncCategoryFromPromptCollection(): void {
    if (this.isRichCategoryPayload(this.categoryAnalysisResponse)) {
      return;
    }
    const promptSd = this.buildStructuredDataFromPromptCollection();
    if (!promptSd) {
      return;
    }
    const base = this.categoryAnalysisResponse;
    const prevSd =
      base?.structured_data && typeof base.structured_data === 'object' && !Array.isArray(base.structured_data)
        ? base.structured_data
        : {};
    const merged = {
      ...prevSd,
      ...promptSd,
      chart_data: { ...((prevSd as any)['chart_data'] || {}), ...(promptSd['chart_data'] || {}) }
    };
    this.categoryAnalysisResponse = {
      ...(base || {}),
      structured_data: merged,
      model_meta: { ...(base?.model_meta || {}), source: 'prompt_responses' }
    };
    this.categoryFromPromptResponses = true;
    this.hydrateCategoryState(merged, this.categoryAnalysisResponse);
  }

  private normPromptCategory(c: unknown): string {
    return String(c ?? '').trim() || 'General';
  }

  private escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private answerMentionsBrand(answer: string, brandName: string): boolean {
    const n = String(brandName || '')
      .replace(/\s*\(YOU\)\s*/gi, '')
      .trim()
      .toLowerCase();
    const t = String(answer || '').toLowerCase();
    if (n.length < 2) {
      return false;
    }
    if (n.length <= 4) {
      try {
        return new RegExp(`\\b${this.escapeRegExp(n)}\\b`, 'i').test(String(answer || ''));
      } catch {
        return t.includes(n);
      }
    }
    return t.includes(n);
  }

  private scoreMentionRate(rows: any[], brandName: string): number {
    if (!Array.isArray(rows) || rows.length === 0) {
      return 0;
    }
    const hits = rows.filter((r) => this.answerMentionsBrand(r.answer_text || '', brandName)).length;
    return Math.round((hits / rows.length) * 100);
  }

  private extractDomainFromCitation(c: any): string | null {
    const u = c?.url || c?.link || c?.href || '';
    if (!u || typeof u !== 'string') {
      return c?.domain || c?.source || null;
    }
    try {
      const url = u.includes('://') ? new URL(u) : new URL('https://' + u);
      return url.hostname.replace(/^www\./, '') || null;
    } catch {
      const m = u.match(/(?:https?:\/\/)?(?:www\.)?([^/\s]+)/i);
      return m ? m[1].replace(/^www\./, '') : null;
    }
  }

  /**
   * Build category-analysis shaped payload from overview_prompt_collection:
   * mention rate per category, competitor lines, citation domains, growing prompts.
   */
  private buildStructuredDataFromPromptCollection(): Record<string, any> | null {
    const pc = this.getPromptCoverageCollection();
    if (!pc) {
      return null;
    }
    let primaryBrand = String(pc['primary_brand'] || '').trim();
    if (!primaryBrand) {
      const fd = this.templateCommonService.getCurrentFilterData();
      const first = fd?.selectedEntities?.[0];
      primaryBrand = (typeof first === 'object' ? first?.name : first) || 'Your brand';
    }
    const primaryRows = this.getPrimaryPromptRowsForCoverage();
    const compNames = this.competitorPromptBrandNames();
    if (primaryRows.length === 0 && compNames.length === 0) {
      return null;
    }

    const catSet = new Set<string>();
    for (const r of primaryRows) {
      catSet.add(this.normPromptCategory(r?.category));
    }
    for (const comp of compNames) {
      for (const r of this.getCompetitorPromptRows(comp)) {
        catSet.add(this.normPromptCategory(r?.category));
      }
    }
    let categories = [...catSet].filter(Boolean).sort((a, b) => a.localeCompare(b));
    if (categories.length === 0) {
      categories = ['General'];
    }

    const primaryScores = categories.map((cat) => {
      const rows = primaryRows.filter((r) => this.normPromptCategory(r?.category) === cat);
      return this.scoreMentionRate(rows, primaryBrand);
    });

    const competitorBrands = compNames.map((name) => ({
      name,
      is_your_brand: false,
      category_scores: categories.map((cat) => {
        const rows = this.getCompetitorPromptRows(name).filter(
          (r) => this.normPromptCategory(r?.category) === cat
        );
        return this.scoreMentionRate(rows, name);
      }),
      composite_score: 0
    }));

    competitorBrands.forEach((b) => {
      const s = b.category_scores;
      b.composite_score = s.length ? Math.round(s.reduce((a, v) => a + v, 0) / s.length) : 0;
    });

    const youBrand = {
      name: primaryBrand,
      is_your_brand: true,
      category_scores: primaryScores,
      composite_score: primaryScores.length
        ? Math.round(primaryScores.reduce((a, v) => a + v, 0) / primaryScores.length)
        : 0
    };

    const brands = [youBrand, ...competitorBrands];

    const category_performance = categories.map((name, i) => ({
      name,
      value: primaryScores[i] ?? 0
    }));

    const avgCompPerCat = categories.map((_, i) => {
      if (competitorBrands.length === 0) {
        return 0;
      }
      const sum = competitorBrands.reduce((a, b) => a + (b.category_scores[i] || 0), 0);
      return sum / competitorBrands.length;
    });
    const youAvg = youBrand.composite_score;
    const compAvgOverall =
      competitorBrands.length === 0
        ? youAvg
        : competitorBrands.reduce((a, b) => a + b.composite_score, 0) / competitorBrands.length;
    let comparison_pct = 0;
    if (compAvgOverall > 0.001) {
      comparison_pct = Math.round(((youAvg - compAvgOverall) / compAvgOverall) * 100);
    }

    const totalPrompts =
      primaryRows.length +
      compNames.reduce((n, c) => n + this.getCompetitorPromptRows(c).length, 0);
    const summary = `Based on ${totalPrompts} AI Mode prompt responses across ${categories.length} categories. Scores show how often each brand is mentioned in answers for prompts in that category.`;

    const growing_prompts: Record<string, any[]> = {};
    for (const cat of categories) {
      const rows = primaryRows.filter((r) => this.normPromptCategory(r?.category) === cat);
      growing_prompts[cat] = rows.map((r) => ({
        prompt: r.prompt || r.query || '',
        prompt_type: r.prompt_type || 'query',
        relevancy: this.answerMentionsBrand(r.answer_text || '', primaryBrand) ? 'Mentioned' : 'Not in answer',
        ai_volume: Array.isArray(r.citations) ? r.citations.length : 0
      }));
    }

    const domainCount = new Map<string, number>();
    const addCites = (rows: any[]) => {
      for (const r of rows) {
        const cites = r.citations;
        if (!Array.isArray(cites)) {
          continue;
        }
        for (const c of cites) {
          const d = this.extractDomainFromCitation(c);
          if (d) {
            domainCount.set(d, (domainCount.get(d) || 0) + 1);
          }
        }
      }
    };
    addCites(primaryRows);
    for (const c of compNames) {
      addCites(this.getCompetitorPromptRows(c));
    }
    const most_cited_domains = [...domainCount.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([name, score]) => ({ name, score }));

    const category_trends = categories.map((cat, idx) => {
      const y = primaryScores[idx];
      const a = avgCompPerCat[idx];
      return {
        summary: `${cat}: your brand in ~${y}% of your category prompts’ answers; competitors average ~${Math.round(a)}% in theirs.`,
        source: 'Prompt responses'
      };
    });

    return {
      brands,
      average_category_performance: {
        average_score: youAvg,
        comparison_pct,
        competitor_count: competitorBrands.length
      },
      average_category_performance_score: youAvg,
      category_performance,
      chart_data: {
        ai_category_insights: { x_axis: categories },
        ai_table_summary: summary,
        composite_score: youAvg
      },
      growing_prompts,
      most_cited_domains,
      category_trends,
      metadata: { data_source: 'prompt_response_aggregation' }
    };
  }

  private prepareChartData(): void {
    if (!this.brands || this.brands.length === 0) {
      this.results = [];
      this.lineChart = [];
      return;
    }

    // Use the same pattern as AI citations - process brands array directly
    this.prepareComboChartData();
  }

  private prepareComboChartData(): void {
    const brands = (this.brands || []).filter((b: any) => b?.name != null && String(b.name).toLowerCase() !== 'none');
    const youBrand = this.findYouBrand(brands);
    const competitorBrands = this.findCompetitorBrands(brands);

    console.log('🔍 Category Analysis Chart: All brands:', brands);
    console.log('🔍 Category Analysis Chart: You brand:', youBrand);
    console.log('🔍 Category Analysis Chart: Competitor brands:', competitorBrands);
    console.log('🔍 Category Analysis Chart: Competitor count:', competitorBrands.length);

    // Get x_axis labels from aiCategoryInsights or use default categories
    const xAxisLabels = this.aiCategoryInsights?.x_axis || [
      'Luxury Vehicle Market',
      'Electric & Hybrid Vehicles', 
      'Autonomous Driving Technology',
      'Performance & Sports Cars',
      'Commercial Vehicles',
      'Digital Services & Connectivity',
      'Sustainability & Green Technology',
      'Customer Experience & Services',
      'Innovation & R&D'
    ];

    // Prepare bar chart data (results) - YOU brand metrics as bars (show all categories)
    if (youBrand && youBrand.category_scores) {
      this.results = xAxisLabels.map((label: string, index: number) => ({
        name: label,
        value: youBrand.category_scores[index] ?? 0
      }));
    } else {
      this.results = [];
    }

    // Prepare line chart data (lineChart) - competitor brands as lines (show all categories)
    if (competitorBrands.length > 0) {
      this.lineChart = competitorBrands.map(brand => ({
        name: brand.name,
        series: xAxisLabels.map((label: string, index: number) => ({
          name: label,
          value: brand.category_scores?.[index] ?? 0
        }))
      }));
      
      console.log('🔍 Category Analysis Chart: Line chart data prepared:', this.lineChart);
    } else {
      this.lineChart = [];
      console.log('🔍 Category Analysis Chart: No competitor brands found, line chart empty');
    }
  }

  private prepareTableData(): void {
    if (!this.aiTableSummary || !this.aiTableSummary.rows) {
      this.tableRows = [];
      return;
    }

    // Initialize table columns
    this.tableColumns = [
      { key: 'category', header: 'Category', className: 'brand-column' },
      { key: 'score', header: 'Score', className: 'metric-column' }
    ];

    // Prepare table rows
    this.tableRows = this.aiTableSummary.rows.map((row: any[], index: number) => ({
      category: row[0],
      score: row[1],
      is_your_brand: false
    }));
  }

  private updateCompositeScore(): void {
    // Try to get composite score from chart_data first (supports number or { value: number })
    const processedData = this.categoryAnalysisResponse?.structured_data || this.categoryAnalysisResponse;
    const chartScore = processedData?.chart_data?.composite_score;
    const scoreValue = typeof chartScore === 'number' ? chartScore : chartScore?.value;
    if (scoreValue != null && !Number.isNaN(scoreValue)) {
      this.compositeScore = Math.round(Number(scoreValue));
      return;
    }
    if (this.brands && this.brands.length > 0) {
      const targetBrand = this.findYouBrand(this.brands) || this.brands[0];
      this.compositeScore = Math.round(targetBrand.composite_score || 0);
    } else {
      this.compositeScore = 0;
    }
  }

  // Helper methods following ai-citations pattern
  private findYouBrand(brands: any[]): any {
    if (!brands || brands.length === 0) return null;
    
    // Look for brand marked as "your brand" or first brand
    const youBrand = brands.find(brand => brand.is_your_brand === true);
    return youBrand || brands[0];
  }

  private findCompetitorBrands(brands: any[]): any[] {
    if (!brands || brands.length <= 1) return [];
    
    // Return all brands except the "you" brand
    const youBrand = this.findYouBrand(brands);
    return brands.filter(brand => brand.name !== youBrand?.name);
  }

  // Get first brand name for chart
  get firstBrandName(): string {
    const youBrand = this.findYouBrand(this.brands || []);
    return youBrand ? youBrand.name : 'Your Brand';
  }

  // Get citations from Category Analysis response
  getCitations(): any[] {
    if (!this.categoryAnalysisResponse) return [];
    
    // Get citations from multiple possible locations
    const citations = this.categoryAnalysisResponse.structured_data?.chart_data?.citations || 
                     this.categoryAnalysisResponse.structured_data?.context?.citations || 
                     this.categoryAnalysisResponse.context?.citations ||
                     this.categoryAnalysisResponse.citations ||
                     [];
    
    return citations.length > 0 ? this.convertToCitations(citations) : [];
  }

  // Get category demand data
  get categoryDemandData(): any[] {
    if (!this.categoryDemand || !this.categoryDemand.series) return [];
    
    return this.categoryDemand.series.map((item: any) => ({
      name: item.name,
      value: item.value
    }));
  }

  // Get subject demand data
  get subjectDemandData(): any[] {
    if (!this.subjectDemandAnalysis || !this.subjectDemandAnalysis.series) return [];
    
    const seriesData = this.subjectDemandAnalysis.series[0];
    if (!seriesData || !seriesData.data) return [];

    // Convert object data to array format
    return Object.entries(seriesData.data)
      .filter(([key, value]) => typeof value === 'number') // Filter out non-numeric values
      .map(([key, value]) => ({
        name: key,
        value: value as number
      }));
  }

  /** Normalize recommendations field: backend may return string or string[]. Always return string[]. */
  private normalizeRecommendationItems(value: string | string[] | undefined | null): string[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value.filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    const s = String(value).trim();
    if (!s) return [];
    // Single string: split by sentence (period + space or newline) for multiple bullets, else one item
    const parts = s.split(/(?<=[.!?])\s+/).map(p => p.trim()).filter(p => p.length > 0);
    return parts.length > 0 ? parts : [s];
  }

  // Get strengths from recommendations (backend may send string or string[])
  getStrengths(): string[] {
    const processedData = this.categoryAnalysisResponse?.structured_data || this.categoryAnalysisResponse;
    const raw = processedData?.chart_data?.recommendations?.strengths;
    return this.normalizeRecommendationItems(raw);
  }

  // Get weaknesses from recommendations (backend may send string or string[])
  getWeaknesses(): string[] {
    const processedData = this.categoryAnalysisResponse?.structured_data || this.categoryAnalysisResponse;
    const raw = processedData?.chart_data?.recommendations?.weaknesses;
    return this.normalizeRecommendationItems(raw);
  }

  // Chart event handlers
  onSelect(event: any): void {
    // Chart selection handler
  }

  onActivate(event: any): void {
    // Chart activation handler
  }

  onDeactivate(event: any): void {
    // Chart deactivation handler
  }

  // Axis formatting methods
  yLeftAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yRightAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yLeftTickFormat(data: number): string {
    return `${data.toLocaleString()}`;
  }

  yRightTickFormat(data: number): string {
    return `${data}%`;
  }

  // Get appropriate chart subtitle
  getChartSubtitle(): string {
    if (!this.brands || this.brands.length === 0) return 'Category Analysis';

    const brands = this.brands;
    if (brands.length === 1) {
      const brand = brands[0];
      return `📊 Single Brand Analysis: ${brand.name} - Detailed category analysis metrics breakdown`;
    } else {
      const youBrand = this.findYouBrand(brands);
      const competitorCount = brands.length - 1;
      if (youBrand) {
        return `Category Analysis: ${youBrand.name} (bars) vs ${competitorCount} competitor${competitorCount > 1 ? 's' : ''} (lines)`;
      } else {
        return `Category Analysis across ${brands.length} brands`;
      }
    }
  }

  // Update responsive view dimensions
  private updateViewByBreakpoint = () => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (width <= 480) {
      this.view = [300, 300];
    } else if (width <= 768) {
      this.view = [400, 350];
    } else if (width <= 1024) {
      this.view = [500, 400];
    } else {
      this.view = [600, 400];
    }
    this.cdr.markForCheck();
  }

  convertToCitations(citations: any[]): any[] {
    if (!citations) return [];
    return citations.map((c: any, index: number) => ({
      id: c.id || index + 1,
      title: c.title || '',
      url: c.url || '',
      snippet: c.snippet || '',
      domain: c.domain || c.source || '',
      source: c.source || c.domain || '',
      confidence: c.confidence || 0.7,
      type: c.type || 'web',
      timestamp: c.timestamp || new Date().toISOString()
    }));
  }

  private transformSourcesData(sources: any[]): any[] {
    return sources.map((source, index) => ({
      title: source.title || source.snippet || `Source ${index + 1}`,
      url: source.url,
      confidence: source.confidence || 0.7,
      snippet: source.snippet || source.title,
      source: source.source || "Tavily",
      category: 'category_analysis',
      id: `source_${index}`
    }));
  }

  getEvidenceSources(): any[] {
    return this.evidenceSources || [];
  }

  // --- New flow (CATEGORY_ANALYSIS_NEW_FLOW_PLAN) ---
  /** structured_data may be at .structured_data (full tab) or the response itself (extracted). */
  private get structuredDataOrResponse(): any {
    const r = this.categoryAnalysisResponse;
    return r?.structured_data ?? r;
  }

  get averageCategoryPerformance(): { average_score: number; comparison_pct: number; competitor_count: number } | null {
    const sd = this.structuredDataOrResponse;
    return sd?.average_category_performance ?? null;
  }

  /** Headline score for Average Category Performance (plan: average_category_performance_score); fallback to average_category_performance.average_score */
  get averageCategoryPerformanceScore(): number | null {
    const sd = this.structuredDataOrResponse;
    const explicit = sd?.average_category_performance_score;
    if (explicit != null && !Number.isNaN(Number(explicit))) return Math.round(Number(explicit));
    const avg = sd?.average_category_performance?.average_score;
    if (avg != null && !Number.isNaN(Number(avg))) return Math.round(Number(avg));
    return null;
  }

  /** When chart_data.ai_table_summary is a string (new flow), use for summary text. */
  get aiTableSummaryText(): string | null {
    const processedData = this.categoryAnalysisResponse?.structured_data || this.categoryAnalysisResponse;
    const raw = processedData?.chart_data?.ai_table_summary;
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return null;
  }

  get categoryPerformanceList(): { name: string; value: number }[] {
    const sd = this.structuredDataOrResponse;
    const list = sd?.category_performance;
    if (!Array.isArray(list)) return this.categoryDemandData;
    return list.map((item: any) => ({
      name: item.name ?? item.label ?? item.category ?? '',
      value: typeof item.value === 'number' ? item.value : Number(item.score ?? item.value ?? 0) || 0
    }));
  }

  get mostCitedDomainsList(): { name: string; score: number }[] {
    const sd = this.structuredDataOrResponse;
    const chartData = sd?.chart_data || this.categoryAnalysisResponse?.chart_data;
    let list = sd?.most_cited_domains;
    if (!Array.isArray(list) && Array.isArray(chartData?.citations)) {
      const citations = chartData.citations as any[];
      if (citations.every((c: any) => c && (c.name != null || c.domain != null))) {
        list = citations.map((c: any) => ({ name: c.name || c.domain || '', score: c.score ?? c.value ?? 0 }));
      }
    }
    if (!Array.isArray(list)) return [];
    return list.map((d: any) => ({ name: d.name || d.domain || '', score: d.score ?? d.value ?? 0 }));
  }

  /** For bar chart: same as mostCitedDomainsList but with value for ngx-charts */
  get mostCitedDomainsChartResults(): { name: string; value: number }[] {
    return this.mostCitedDomainsList.map(d => ({ name: d.name, value: d.score }));
  }

  /** Backend may send category -> string (summary) or category -> array of prompt rows */
  get growingPromptsByCategory(): { [category: string]: string | any[] } {
    const sd = this.structuredDataOrResponse;
    const gp = sd?.growing_prompts;
    if (gp && typeof gp === 'object' && !Array.isArray(gp)) return gp as { [category: string]: string | any[] };
    return {};
  }

  get categoryTrendsList(): { summary: string; source?: string }[] {
    const sd = this.structuredDataOrResponse;
    const chartData = sd?.chart_data || this.categoryAnalysisResponse?.chart_data;
    const list = sd?.category_trends ?? chartData?.category_trends;
    if (!Array.isArray(list)) return [];
    return list
      .map((t: any) => ({ summary: t?.summary ?? t?.text ?? String(t), source: t?.source }))
      .filter((t: { summary: string }) => t.summary && t.summary.trim().length > 0);
  }

  /** Chart data: your score vs category performance per category (Section 3) */
  get categoryVsBrandChartResults(): { name: string; value: number }[] {
    const youBrand = this.findYouBrand(this.brands || []);
    const catPerf = this.categoryPerformanceList;
    if (!youBrand?.category_scores?.length && !catPerf.length) return [];
    const labels = this.aiCategoryInsights?.x_axis || catPerf.map(c => c.name);
    return labels.map((label: string, i: number) => ({
      name: label,
      value: youBrand?.category_scores?.[i] ?? 0
    }));
  }

  get categoryVsBrandChartLineSeries(): { name: string; series: { name: string; value: number }[] } | null {
    const catPerf = this.categoryPerformanceList;
    if (!catPerf.length) return null;
    return {
      name: 'Category performance',
      series: catPerf.map(c => ({ name: c.name, value: c.value }))
    };
  }

  getGrowingPromptCategories(): string[] {
    const gp = this.growingPromptsByCategory;
    if (!gp || typeof gp !== 'object') return [];
    return Object.keys(gp).filter(c => {
      const v: string | any[] = gp[c];
      if (Array.isArray(v)) return v.length > 0;
      return v != null && typeof v === 'string' && v.trim().length > 0;
    });
  }

  /** Backend may send growing_prompts[category] as string (summary) or array of { prompt, prompt_type, relevancy, ai_volume, ... } (AI Visibility shape). */
  getGrowingPromptsForCategory(category: string): any[] {
    const raw: string | any[] = this.growingPromptsByCategory[category];
    if (Array.isArray(raw)) return raw;
    if (raw != null && typeof raw === 'string' && raw.trim()) {
      return [{ prompt: raw.trim(), prompt_type: '-', relevancy: '-', ai_volume: '-', ranking: 'Yes' }];
    }
    return [];
  }

  /** Canonical semantic prompt types for display. */
  private static readonly SEMANTIC_TYPES = [
    'Informational', 'Purchase', 'Instructional', 'Clarification', 'Analytical', 'Creative', 'Opinion-based'
  ] as const;

  /** Type of prompts: map prompt_type to semantic types (Informational, Purchase, Instructional, etc.). */
  getPromptTypeDisplay(row: any): string {
    if (!row) return '-';
    const v = (row.prompt_type ?? row.promptType ?? row.type ?? row.type_of_prompt ?? '') as string;
    if (v == null || v === '') return '-';
    const s = String(v).trim();
    const lower = s.toLowerCase().replace(/-/g, '_').replace(/ /g, '_');
    const canonical = GenericCategoryAnalysisTabComponent.SEMANTIC_TYPES.find(
      t => t.toLowerCase().replace(/-/g, '_') === lower
    );
    if (canonical) return canonical;
    const mapping: Record<string, string> = {
      generic: 'Informational',
      non_branded: 'Informational',
      query: 'Informational',
      branded: 'Purchase',
      brand: 'Purchase',
      purchase_intent: 'Purchase',
      instructional: 'Instructional',
      clarification: 'Clarification',
      analytical: 'Analytical',
      comparison: 'Analytical',
      creative: 'Creative',
      opinion_based: 'Opinion-based',
      'opinion-based': 'Opinion-based',
    };
    return mapping[lower] ?? 'Informational';
  }

  /** Relevancy: number or string from backend. */
  getRelevancyDisplay(row: any): string | number {
    if (!row) return '-';
    const v = row.relevancy;
    return v != null && v !== '' ? v : '-';
  }

  /** AI Volume: show current value when backend sends { current, previous_month, ... }, else raw value. */
  getAiVolumeDisplay(row: any): string | number {
    if (!row) return '-';
    const av = row.ai_volume ?? row.aiVolume;
    if (av == null) return '-';
    if (typeof av === 'object' && 'current' in av) return av.current ?? '-';
    return av;
  }

  getPromptCoverageCollection(): Record<string, unknown> | null {
    const pc = this.overviewPromptCollection;
    return pc && typeof pc === 'object' ? pc : null;
  }

  competitorPromptBrandNames(): string[] {
    const c = this.getPromptCoverageCollection()?.['competitors'] as Record<string, unknown[]> | undefined;
    if (!c || typeof c !== 'object') return [];
    return Object.keys(c);
  }

  getCompetitorPromptRows(comp: string): any[] {
    const c = this.getPromptCoverageCollection()?.['competitors'] as Record<string, any[]> | undefined;
    const rows = c?.[comp];
    return Array.isArray(rows) ? rows : [];
  }

  getPrimaryPromptRowsForCoverage(): any[] {
    const pq = this.getPromptCoverageCollection()?.['primary_queries'];
    return Array.isArray(pq) ? pq : [];
  }
}

